import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useTenant } from "../../../Contexts/TenantContext";
import { orderService } from "../../../Api/Services/orderService";
import { paymentService } from "../../../Api/Services/paymentService";
import { createOrderHubConnection } from "../../../Api/orderHub";
import { Order } from "../../../Models/Order";
import { ShippingDetails } from "../../../Models/ShippingDetails";
import { Address } from "../../../Models/Address";
import { BankingBilletChargeResponse, PaymentOptions, PixChargeResponse } from "../../../Models/Payment";
import { PaymentMethod } from "../../../Enums/PaymentMethod";
import { ShippingMethod } from "../../../Enums/ShippingMethod";
import { PaymentStatus } from "../../../Enums/PaymentStatus";
import { OrderStatus } from "../../../Enums/OrderStatus";
import { CreditCardData } from "../../../Types/CreditCardDataType";
import { generatePaymentToken } from "../../../Utils/efiBankPayment";
import { needsPaymentSync, PaymentSyncSnapshot, syncPaymentMethod } from "../../../Utils/paymentSync";
import { FaCheckCircle, FaCreditCard, FaRegEye } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import ProgressBar from "../../../Components/ProgressBar/ProgressBar";
import OrderSummary from "../../../Components/OrderSummary/OrderSummary";
import FooterPolicies from "../../../Components/FooterPolicies/FooterPolicies";
import FooterInfo from "../../../Components/FooterInfo/FooterInfo";
import ReviewStep from "../../../Components/ReviewStep/ReviewStep";
import PaymentStep from "../../../Components/PaymentStep/PaymentStep";
import OrderCompletedStep from "../../../Components/OrderCompletedStep/OrderCompletedStep";
import PixPaymentStep from "../../../Components/PixPaymentStep/PixPaymentStep";
import BankingBilletStep from "../../../Components/BankingBilletStep/BankingBilletStep";
import OrderNavigation from "../../../Components/OrderNavigation/OrderNavigation";
import LoadingOverlay from "../../../Components/LoadingOverlay/LoadingOverlay";

const buildBaseSteps = () => ([
  { label: "Resumo", icon: <FaRegEye /> },
  { label: "Pagamento", icon: <FaCreditCard /> }
]);

const UserCompleteOrderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenantPreference, tenantShippingProviderConfig } = useTenant();
  const baseSteps = useMemo(() => buildBaseSteps(), []);

  const [loading, setLoading] = useState<boolean>(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState<boolean>(true);
  const [order, setOrder] = useState<Order | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [shippingDetails, setShippingDetails] = useState<ShippingDetails | null>(null);
  const [paymentOptions, setPaymentOptions] = useState<PaymentOptions | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);

  const [creditCardData, setCreditCardData] = useState<CreditCardData>({
    cardNumber: "",
    cardName: "",
    cardCvv: "",
    cardExpiration: "",
    holderDocument: "",
    selectedInstallments: 1,
    cardBrand: null
  });
  const [pixPaymentResult, setPixPaymentResult] = useState<PixChargeResponse | null>(null);
  const [bankingBilletResult, setBankingBilletResult] = useState<BankingBilletChargeResponse | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState<boolean>(false);
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false);
  const hubRef = useRef<ReturnType<typeof createOrderHubConnection> | null>(null);
  const orderIdRef = useRef<string | null>(null);
  const finalStepIndexRef = useRef(0);
  const paymentMethodRef = useRef<PaymentMethod | null>(null);

  const SkeletonBlock = ({ height = 16, width = "100%" }: { height?: number | string; width?: number | string }) => (
    <div className="animate-pulse bg-gray-200 rounded w-full" style={{ height, maxWidth: width }} />
  );

  const renderSkeleton = () => (
    <div className="flex flex-col gap-9 px-4 sm:px-14">
      <div className="flex items-center justify-center">
        <SkeletonBlock height={24} width={280} />
      </div>
      <div className="flex flex-col lg:flex-row gap-7">
        <div className="flex-1 space-y-4">
          <SkeletonBlock height={40} />
          <SkeletonBlock height={220} />
          <SkeletonBlock height={40} />
        </div>
        <div className="flex flex-col gap-4 lg:flex-[0.5]">
          <SkeletonBlock height={200} />
          <SkeletonBlock height={44} />
          <SkeletonBlock height={44} />
        </div>
      </div>
    </div>
  );


  const refreshOrder = useCallback(async (orderId: string) => {
    const orderData = await orderService.get(orderId);
    if (!orderData) {
      toast.error("Não foi possível carregar o pedido.");
      return;
    }

    setOrder(orderData);
    setAddress(orderData.address);
    setShippingDetails(orderData.shippingDetails);
    setPaymentOptions(orderData.paymentOptions!);
  }, []);

  const handlePaymentMethodChange = async (method: PaymentMethod) => {
    setSelectedPaymentMethod(method);
    if (method !== PaymentMethod.CreditCard) {
      setCreditCardData({
        cardNumber: "",
        cardName: "",
        cardCvv: "",
        cardExpiration: "",
        holderDocument: "",
        selectedInstallments: 1,
        cardBrand: null
      });
    }
  };

  const [steps, setSteps] = useState(baseSteps);
  const isAwaitingConfirmation =
    !isPaymentConfirmed &&
    (selectedPaymentMethod === PaymentMethod.Pix ||
      selectedPaymentMethod === PaymentMethod.BankingBillet);
  const isFinalStep = steps.length > baseSteps.length && currentStep === steps.length - 1;

  const buildSteps = useCallback((method: PaymentMethod | null) => {
    if (method === PaymentMethod.Pix) {
      return [
        ...baseSteps,
        { label: "PIX", icon: <FaRegEye /> },
        { label: "Concluir", icon: <FaCheckCircle /> }
      ];
    }

    if (method === PaymentMethod.BankingBillet) {
      return [
        ...baseSteps,
        { label: "Boleto", icon: <FaRegEye /> },
        { label: "Concluir", icon: <FaCheckCircle /> }
      ];
    }

    if (method != null) {
      return [
        ...baseSteps,
        { label: "Concluir", icon: <FaCheckCircle /> }
      ];
    }

    return baseSteps;
  }, [baseSteps]);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) {
        return;
      }

      try {
        setIsLoadingOrder(true);
        await refreshOrder(id);
      } finally {
        setIsLoadingOrder(false);
      }
    };

    fetchOrder();
  }, [id, refreshOrder]);

  useEffect(() => {
    orderIdRef.current = order?.id ?? null;
  }, [order?.id]);

  useEffect(() => {
    finalStepIndexRef.current = steps.length - 1;
  }, [steps.length]);

  useEffect(() => {
    paymentMethodRef.current = selectedPaymentMethod;
  }, [selectedPaymentMethod]);

  useEffect(() => {
    const nextSteps = buildSteps(selectedPaymentMethod);

    setSteps(nextSteps);
    setCurrentStep(prev => Math.min(prev, nextSteps.length - 1));
  }, [buildSteps, selectedPaymentMethod]);

  useEffect(() => {
    if (!order) {
      return;
    }

    if (order.status === OrderStatus.Paid || order.status === OrderStatus.Shipped) {
      setIsPaymentConfirmed(true);
    }
  }, [order]);

  useEffect(() => {
    if (hubRef.current) {
      return;
    }

    const hub = createOrderHubConnection();
    hubRef.current = hub;

    const handleStatusChanged = (payload: { orderId?: string; status?: string }) => {
      if (!payload?.orderId || payload.orderId !== orderIdRef.current) {
        return;
      }

      if (payload.status === "Paid" || payload.status === "Shipped") {
        const method = paymentMethodRef.current;
        if (
          method === PaymentMethod.Pix ||
          method === PaymentMethod.BankingBillet
        ) {
          setIsPaymentConfirmed(true);
          setIsProcessingPayment(false);
          setCurrentStep(finalStepIndexRef.current);
          if (orderIdRef.current) {
            void refreshOrder(orderIdRef.current);
          }
        }
      }
    };

    hub.on("OrderStatusChanged", handleStatusChanged);

    hub.start().catch(() => {
      // Ignore hub start errors; manual refresh still works
    });

    return () => {
      hub.off("OrderStatusChanged", handleStatusChanged);
      hub.stop();
      hubRef.current = null;
    };
  }, [refreshOrder]);

  useEffect(() => {
    const finalizeExternalCheckout = async () => {
      if (!order?.id) {
        return;
      }

    };

    void finalizeExternalCheckout();
  }, [order?.id, navigate, refreshOrder]);

  const syncPaymentMethodForOrder = async () => {
    if (!order?.id || selectedPaymentMethod == null) {
      return false;
    }

    try {
      const currentSnapshot: PaymentSyncSnapshot = {
        paymentMethod: order.paymentMethod ?? null,
        creditCardInstallments: order.creditCardInstallments ?? null
      };
      const targetSnapshot: PaymentSyncSnapshot = {
        paymentMethod: selectedPaymentMethod,
        creditCardInstallments: selectedPaymentMethod === PaymentMethod.CreditCard
          ? creditCardData.selectedInstallments ?? null
          : null
      };

      if (!needsPaymentSync(currentSnapshot, targetSnapshot)) {
        return true;
      }

      const synced = await syncPaymentMethod({
        orderId: order.id,
        paymentMethod: selectedPaymentMethod,
        creditCardInstallments: targetSnapshot.creditCardInstallments ?? null
      });

      if (!synced) {
        toast.error("Não foi possível atualizar o método de pagamento. Tente novamente.");
        return false;
      }

      await refreshOrder(order.id);
      return true;
    } catch {
      toast.error("Não foi possível atualizar o método de pagamento. Tente novamente.");
      return false;
    }
  };

  const handleAddPaymentStep = () => {
    setSteps(buildSteps(selectedPaymentMethod));
  };

  const nextStep = async () => {
    if (currentStep === 0) {
      setCurrentStep(1);
      return;
    }

    if (currentStep === 1) {
      if (selectedPaymentMethod == null) {
        toast.info("Selecione um método de pagamento para continuar.");
        return;
      }

      if (selectedPaymentMethod === PaymentMethod.CreditCard) {
        const { cardNumber, cardName, cardCvv, cardExpiration, holderDocument } = creditCardData;

        if (!cardNumber || !cardName || !cardCvv || !cardExpiration || !holderDocument) {
          toast.error("Por favor, preencha todos os campos do cartão de crédito para continuar.");
          return;
        }
      }

      handleAddPaymentStep();
      const synced = await syncPaymentMethodForOrder();
      if (!synced) {
        return;
      }
      await handleCompleteOrder();
      return;
    }

    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const prevStep = () => {
    if (currentStep === 0) {
      navigate("/pedidos");
    } else {
      setCurrentStep((prev) => Math.max(prev - 1, 0));
    }
  };

  const handleCompleteOrder = async (): Promise<void> => {
    if (selectedPaymentMethod == null || !order) {
      return;
    }

    const method = selectedPaymentMethod as PaymentMethod;

    try {
      setLoading(true);

      switch (method) {
        case PaymentMethod.CreditCard: {
          const tokenResult = await generatePaymentToken(creditCardData);

          if (!tokenResult) {
            toast.error("Não foi possível realizar o pagamento, tente novamente mais tarde.");
            setLoading(false);
            return;
          }

          const { paymentToken, cardMask } = tokenResult;

          if (paymentToken) {
            const chargeResult = await paymentService.createCreditCardCharge({
              orderId: order.id,
              paymentToken,
              installments: creditCardData.selectedInstallments!,
              maskedCardNumber: cardMask
            });

            if (!chargeResult) {
              toast.error("Não foi possível processar o pagamento. Tente novamente.");
              return;
            }

            if (chargeResult.status === PaymentStatus.Paid) {
              setIsProcessingPayment(false);
              setIsPaymentConfirmed(true);
              setCurrentStep(2);
            } else if (chargeResult.status === PaymentStatus.Processing) {
              setIsProcessingPayment(true);
              setCurrentStep(2);
            } else {
              if (chargeResult.retryable && chargeResult.chargeId) {
                const retryResult = await paymentService.retryCreditCardCharge({
                  chargeId: chargeResult.chargeId,
                  orderId: order.id,
                  paymentToken,
                  installments: creditCardData.selectedInstallments!,
                  maskedCardNumber: cardMask
                });

                if (retryResult) {
                  if (retryResult.status === PaymentStatus.Paid) {
                    setIsProcessingPayment(false);
                    setIsPaymentConfirmed(true);
                    setCurrentStep(2);
                    return;
                  }
                  if (retryResult.status === PaymentStatus.Processing) {
                    setIsProcessingPayment(true);
                    setCurrentStep(2);
                    return;
                  }
                  const reason = retryResult.refusalReason ?? chargeResult.refusalReason ?? "Pagamento não autorizado. Tente novamente ou escolha outro método.";
                  toast.error(reason);
                } else {
                  const reason = chargeResult.refusalReason ?? "Pagamento não autorizado. Tente novamente ou escolha outro método.";
                  toast.error(reason);
                }
              } else {
                const reason = chargeResult.refusalReason ?? "Pagamento não autorizado. Tente novamente ou escolha outro método.";
                toast.error(reason);
              }
            }
          }
          setLoading(false);
          return;
        }

        case PaymentMethod.Pix: {
          const pixResult = await paymentService.createPixCharge({ orderId: order.id });
          setPixPaymentResult(pixResult);
          setCurrentStep(2);
          setLoading(false);
          return;
        }

        case PaymentMethod.BankingBillet: {
          const billetResult = await paymentService.createBankingBilletCharge({ orderId: order.id });
          setBankingBilletResult(billetResult);
          setCurrentStep(2);
          setLoading(false);
          return;
        }

        default:
          setLoading(false);
          return;
      }
    } catch {
      toast.error("Não foi possível completar o pedido, tente novamente mais tarde.");
    } finally {
      setLoading(false);
    }
  };

  const getFinalPrice = () => {
    if (
      selectedPaymentMethod === PaymentMethod.CreditCard && 
      creditCardData?.selectedInstallments && 
      creditCardData.selectedInstallments > 1
    ) {
      const installment = paymentOptions?.creditCardInstallments.find(
        i => i.installments === creditCardData.selectedInstallments
      );
      return installment?.totalPrice ?? order?.finalPrice ?? 0;
    }
    return order?.finalPrice ?? 0;
  };

  const getBankingBilletFee = () => {
    const fee = tenantPreference?.bankingBilletFee ?? 0;
    if (selectedPaymentMethod === PaymentMethod.BankingBillet) {
      return fee;
    }

    return 0;
  };

  return (
    <div className={`min-h-screen flex flex-col bg-gray-50`}>
      <SmartNavbar />
      {loading && <LoadingOverlay topOffset="112px" />}
      <div className={`w-full flex-grow py-10 ${isFinalStep ? "flex flex-col" : ""}`}>
        {isLoadingOrder ? (
          renderSkeleton()
        ) : (
          <div className={`flex flex-col ${isFinalStep ? "flex-1" : "gap-9"}`}>
            <ProgressBar steps={steps} currentStep={currentStep} />

            <div className={`flex flex-col lg:flex-row gap-7 px-4 sm:px-14`}>
              {currentStep === 0 && (
                <ReviewStep 
                  selectedAddress={`${address?.street}, ${address?.number} - ${address?.neighborhood} - ${address?.city}/${address?.state} - CEP: ${address?.postalCode}`}
                  selectedShipping={shippingDetails ? {
                    methodType: order?.shippingMethod!,
                    price: shippingDetails.price ?? null,
                    deliveryTime: shippingDetails.estimatedTimeInDays ?? null
                  } : null}
                  isOrderPage={true}
                  order={order}
                />
              )}

              {currentStep === 1 && (
                <PaymentStep
                  selectedPaymentMethod={selectedPaymentMethod}
                  onSelectPaymentMethod={handlePaymentMethodChange}
                  creditCardData={creditCardData}
                  onCreditCardDataChange={setCreditCardData}
                  orderPrice={getFinalPrice()}
                />
              )}

              {selectedPaymentMethod === PaymentMethod.Pix && currentStep === 2 && pixPaymentResult && (
                <PixPaymentStep pixData={pixPaymentResult} />
              )}

              {selectedPaymentMethod === PaymentMethod.BankingBillet && currentStep === 2 && bankingBilletResult && (
                <BankingBilletStep
                  billetUrl={bankingBilletResult.billetLink}
                  billetPdf={bankingBilletResult.billetPdf}
                  expiration={bankingBilletResult.expiration}
                />
              )}

            {isFinalStep && (
              <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-14">
                <OrderCompletedStep
                  orderNumber={order?.orderNumber!}
                  awaitingConfirmation={isAwaitingConfirmation}
                  processingPayment={isProcessingPayment}
                  isStorePickup={order?.shippingMethod === ShippingMethod.StorePickup}
                  pickupDays={tenantShippingProviderConfig?.storePickupEstimatedTimeInDays}
                />
              </div>
              )}
              
            {!isFinalStep && (
              <div className="flex flex-col gap-4 lg:flex-[0.5]">
                {(() => {
                  const isPaymentCreatedStep =
                    currentStep === 2 &&
                    (selectedPaymentMethod === PaymentMethod.Pix ||
                      selectedPaymentMethod === PaymentMethod.BankingBillet);

                  return (
                    <>
                      {!isPaymentCreatedStep && (
                        <OrderSummary
                          originalTotalPrice={order?.originalTotalPrice}
                          orderProductDiscount={order?.productDiscount}
                          orderCouponDiscount={order?.couponDiscount}
                          orderShippingPrice={shippingDetails?.price}
                          orderShippingMethod={order?.shippingMethod ?? null}
                          orderBankingBilletFee={getBankingBilletFee()}
                          orderBaseBankingBilletFee={tenantPreference?.bankingBilletFee ?? null}
                          orderFinalPrice={getFinalPrice()}
                          orderInterest={paymentOptions?.creditCardInstallments.find(
                            i => i.installments === creditCardData.selectedInstallments
                          )?.totalInterest ?? 0}
                        />
                      )}
                      <OrderNavigation
                        onNext={nextStep}
                        onBack={prevStep}
                        hideBackButton={isPaymentCreatedStep}
                      />
                    </>
                  );
                })()}
              </div>
            )}
            </div>
          </div>
        )}
      </div>
      <FooterPolicies />
      <FooterInfo />
    </div>
  );
};

export default UserCompleteOrderPage;
